/**
 * Las reglas del import de listas de precios, sin React.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * Esta es la pantalla donde mas plata se rompe de un solo click. Un import mal
 * aplicado reescribe el costo de 400 productos, y `trg_productos_precio`
 * recalcula el precio de venta de cada uno sin preguntar. El unico freno real es
 * que una persona haya MIRADO las filas peligrosas antes de confirmar.
 *
 * Mirarlas todas no pasa: nadie revisa 400 lineas de un PDF. Lo que si pasa es
 * revisar las 12 que estan mal. Todo lo de aca existe para producir esas 12:
 *
 *   · `ordenarParaRevisar()` sube arriba lo que puede estar mal (un +80% suele
 *     ser un punto decimal mal leido del PDF, no un aumento).
 *   · `calcularImpacto()` contesta "que pasa si aprieto Aplicar" ANTES de
 *     apretarlo, y no suma pesos con dolares para que el numero quede prolijo.
 *   · `validarAccion()` frena en la pantalla lo mismo que el backend frena con
 *     un 422, para que el error se vea al elegir y no despues de escribir.
 *
 * Nada de aca toca el DOM: se prueba con filas sueltas, que es exactamente como
 * hay que probar algo que mueve costos.
 *
 * LA FORMA DE LOS DATOS
 * ---------------------
 * `lib/tipos.ts` no modela el import de listas, asi que las interfaces viven
 * aca, espejadas contra `supabase/migrations/0007_listas_import.sql` y contra
 * los esquemas Zod de `packages/backend/src/repos/listas.ts`. Los `numeric` se
 * tipan `Numerico` igual que en el resto del frontend: Postgres los serializa
 * como string y `aNumero()` es el unico lugar donde eso se resuelve.
 */
import { convertir, formatearNumero, type Moneda } from "@tora/shared";

import { aNumero, type Numerico, type Proveedor } from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// Los datos, tal como los devuelve el backend
// ─────────────────────────────────────────────────────────────────────────────

export const ESTADOS_IMPORT = [
  "PENDIENTE",
  "PROCESANDO",
  "COMPLETADO",
  "ERROR",
  "APLICADO",
  "DESCARTADO",
] as const;
export type EstadoImport = (typeof ESTADOS_IMPORT)[number];

export const TIPOS_MATCH = [
  "CODIGO_EXTERNO",
  "CODIGO_INTERNO",
  "NOMBRE",
  "MANUAL",
  "NUEVO",
  "SIN_MATCH",
] as const;
export type TipoMatch = (typeof TIPOS_MATCH)[number];

/** Que hace cada fila al aplicar. Es lo unico que el usuario decide de verdad. */
export const ACCIONES = ["ACTUALIZAR", "CREAR", "IGNORAR"] as const;
export type AccionFila = (typeof ACCIONES)[number];

/** MIXTA es un resultado legitimo del extractor y el mas peligroso de todos. */
export type MonedaDetectada = "ARS" | "USD" | "MIXTA";

export interface ImportLista {
  id: string;
  proveedor_id: number | null;
  archivo_nombre: string;
  archivo_bytes: Numerico;
  archivo_url: string | null;
  estado: EstadoImport;
  moneda_detectada: MonedaDetectada | null;
  notas: string | null;
  error_codigo: string | null;
  error_mensaje: string | null;
  modelo: string | null;
  tokens_entrada: number | null;
  tokens_salida: number | null;
  duracion_ms: number | null;
  filas_total: number;
  lote_id: string | null;
  aplicado_at: string | null;
  created_at: string;
  finished_at: string | null;
  /** Lo que dijo la lectura del archivo (0150). Opcionales: una base vieja no los manda. */
  tipo_documento?: "LISTA" | "COMPROBANTE";
  proveedor_detectado?: string | null;
  comprobante_detectado?: string | null;
  trae_precio_venta?: boolean;
}

/** Una fila de `v_lista_revision`: lo que se mira antes de confirmar. */
export interface FilaRevision {
  id: number;
  import_id: string;
  orden: number;
  codigo_externo: string | null;
  descripcion: string;
  costo: Numerico;
  moneda: Moneda;
  producto_id: number | null;
  match_tipo: TipoMatch | null;
  match_confianza: Numerico | null;
  costo_anterior: Numerico | null;
  moneda_anterior: Moneda | null;
  variacion_pct: Numerico | null;
  accion: AccionFila;
  aplicada: boolean;
  error: string | null;
  codigo_interno: string | null;
  producto_nombre: string | null;
  precio_actual: Numerico | null;
  precio_moneda: Moneda | null;
  precio_resultante: Numerico | null;
  /** 0150: precio puesto a mano (no cambia), costo que fija el precio hoy, activo. */
  precio_manual?: Numerico | null;
  costo_precio_actual?: Numerico | null;
  producto_activo?: boolean | null;
}

/** Lo que devuelve `matchear_lista()`, recalculado en cada lectura. */
export interface ResumenMatching {
  filas: number;
  con_match: number;
  sin_match: number;
  por_codigo: number;
  por_nombre: number;
  propuestas_actualizar: number;
}

export interface ResultadoAplicar {
  import_id: string;
  lote_id: string;
  actualizados: number;
  creados: number;
}

export interface ResultadoRevertir {
  lote_revertido: string;
  lote_reversion: string;
  productos: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Umbrales
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A partir de aca la variacion merece una mirada; a partir de GRAVE, merece
 * desconfianza.
 *
 * Los numeros no son arbitrarios. En este rubro un aumento de lista real vive
 * entre 5% y 25%. Un +60% casi nunca es un aumento: es una coma leida como
 * punto, una fila corrida, o un producto matcheado contra otro. El umbral no
 * bloquea nada —hay aumentos reales de 60%— pero decide que se ve primero.
 */
export const VARIACION_ALERTA = 25;
export const VARIACION_GRAVE = 60;

/**
 * Debajo de esta confianza, el match lo hizo la similitud de nombre y no un
 * codigo. `matchear_lista()` los deja en IGNORAR justamente por eso.
 */
export const CONFIANZA_DUDOSA = 0.6;

/** Formatos que acepta `extraccion-listas.ts`, y el tope de tamano del backend. */
export const EXTENSIONES_ACEPTADAS = [
  ".pdf",
  ".xlsx",
  ".xls",
  ".csv",
  ".txt",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".gif",
] as const;
export const LIMITE_BYTES_ARCHIVO = 24 * 1024 * 1024;

// ─────────────────────────────────────────────────────────────────────────────
// Lectura de una fila
// ─────────────────────────────────────────────────────────────────────────────

export const TEXTO_MATCH: Record<TipoMatch, { corto: string; largo: string }> = {
  CODIGO_EXTERNO: {
    corto: "código del proveedor",
    largo: "El código de la lista coincide exacto con el código de proveedor del producto.",
  },
  CODIGO_INTERNO: {
    corto: "código interno",
    largo: "El código de la lista coincide con el código interno del local.",
  },
  NOMBRE: {
    corto: "nombre parecido",
    largo:
      "No había código: se emparejó por parecido de nombre. Nadie verificó que sea el mismo producto.",
  },
  MANUAL: {
    corto: "elegido a mano",
    largo: "Lo asignó una persona en esta pantalla.",
  },
  NUEVO: { corto: "producto nuevo", largo: "No existe en el catálogo; se daría de alta." },
  SIN_MATCH: {
    corto: "sin match",
    largo: "No se encontró ningún producto parecido. No se toca nada salvo que lo crees.",
  },
};

export function variacionDe(fila: FilaRevision): number | null {
  return fila.variacion_pct === null || fila.variacion_pct === undefined
    ? null
    : aNumero(fila.variacion_pct);
}

export function confianzaDe(fila: FilaRevision): number | null {
  return fila.match_confianza === null || fila.match_confianza === undefined
    ? null
    : aNumero(fila.match_confianza);
}

/**
 * La fila matchea un producto que hoy se costea en OTRA moneda.
 *
 * Es el caso mas caro del sistema y por eso `matchear_lista()` deja
 * `variacion_pct` en null: comparar 150 USD contra 150.000 ARS no significa
 * nada. Aplicar esa fila no cambia un costo, cambia la moneda en la que ese
 * producto vive, y el precio de venta se recalcula con la cotizacion.
 */
export function cambiaLaMoneda(fila: FilaRevision): boolean {
  return (
    fila.producto_id !== null &&
    fila.moneda_anterior !== null &&
    fila.moneda_anterior !== fila.moneda
  );
}

export type Riesgo = "GRAVE" | "ALTO" | "MEDIO" | "NINGUNO";

/**
 * Cuanta atencion pide esta fila.
 *
 * No decide nada por si sola: alimenta el orden de revision y el contador de
 * "filas que conviene mirar" del panel de impacto.
 */
export function riesgoDeFila(fila: FilaRevision): Riesgo {
  if (fila.error !== null) return "GRAVE";
  if (cambiaLaMoneda(fila)) return "GRAVE";

  const variacion = variacionDe(fila);
  if (variacion !== null && Math.abs(variacion) >= VARIACION_GRAVE) return "GRAVE";
  if (variacion !== null && Math.abs(variacion) >= VARIACION_ALERTA) return "ALTO";

  const confianza = confianzaDe(fila);
  if (fila.match_tipo === "NOMBRE" && confianza !== null && confianza < CONFIANZA_DUDOSA) {
    return "ALTO";
  }
  if (fila.match_tipo === "NOMBRE") return "MEDIO";
  if (fila.producto_id === null) return "MEDIO";
  return "NINGUNO";
}

/** Filas que no deberian aplicarse sin haberlas leido de verdad. */
export function esSospechosa(fila: FilaRevision): boolean {
  const riesgo = riesgoDeFila(fila);
  return riesgo === "GRAVE" || riesgo === "ALTO";
}

/**
 * Prioridad de revision, de 0 (mirala ya) a 6 (mirala si te sobra tiempo).
 *
 * El orden de los tramos es el orden en que se rompe la plata:
 *   0 · la fila ya viene con error, o cambia la moneda del costo
 *   1 · variacion enorme: casi siempre un error de lectura del archivo
 *   2 · variacion grande pero posible
 *   3 · match por nombre flojo (el sistema adivino y adivino mal seguido)
 *   4 · match por nombre
 *   5 · sin match: hay que decidir si se crea
 *   6 · match por codigo con variacion normal: el caso aburrido
 */
export function prioridadDeRevision(fila: FilaRevision): number {
  if (fila.error !== null || cambiaLaMoneda(fila)) return 0;

  const variacion = variacionDe(fila);
  if (variacion !== null && Math.abs(variacion) >= VARIACION_GRAVE) return 1;
  if (variacion !== null && Math.abs(variacion) >= VARIACION_ALERTA) return 2;

  const confianza = confianzaDe(fila);
  if (fila.match_tipo === "NOMBRE") {
    return confianza !== null && confianza < CONFIANZA_DUDOSA ? 3 : 4;
  }
  if (fila.producto_id === null) return 5;
  return 6;
}

/**
 * El orden en el que conviene leer la revision.
 *
 * NO es el orden del archivo. El orden del archivo sirve para cotejar contra el
 * PDF; para decidir sirve este, que pone arriba lo que puede costar plata.
 * Dentro del mismo tramo manda la variacion mas grande, y a igualdad de todo se
 * respeta el orden original para que la lista no baile entre renders.
 */
export function ordenarParaRevisar(filas: ReadonlyArray<FilaRevision>): FilaRevision[] {
  return [...filas].sort((a, b) => {
    const pa = prioridadDeRevision(a);
    const pb = prioridadDeRevision(b);
    if (pa !== pb) return pa - pb;

    const va = Math.abs(variacionDe(a) ?? 0);
    const vb = Math.abs(variacionDe(b) ?? 0);
    if (va !== vb) return vb - va;

    return a.orden - b.orden;
  });
}

/** El orden del archivo, para cuando alguien esta cotejando contra el PDF. */
export function ordenarComoElArchivo(filas: ReadonlyArray<FilaRevision>): FilaRevision[] {
  return [...filas].sort((a, b) => a.orden - b.orden);
}

export interface FiltrosRevision {
  /** "TODAS" o una accion concreta. */
  accion?: AccionFila | "TODAS";
  /** Solo las que conviene mirar antes de aplicar. */
  soloSospechosas?: boolean;
  /** Solo las que no encontraron producto. */
  soloSinMatch?: boolean;
  /** Busca en la descripcion de la lista, en el producto y en los codigos. */
  texto?: string;
}

/**
 * El filtro de la tabla.
 *
 * Vive con la logica y no adentro del componente porque decide QUE se ve antes
 * de aplicar: un filtro que esconde por error las filas peligrosas es tan caro
 * como un orden mal hecho.
 */
export function filtrarFilas(
  filas: ReadonlyArray<FilaRevision>,
  filtros: FiltrosRevision,
): FilaRevision[] {
  const busqueda = (filtros.texto ?? "").trim().toLowerCase();

  return filas.filter((fila) => {
    if (filtros.accion && filtros.accion !== "TODAS" && fila.accion !== filtros.accion) {
      return false;
    }
    if (filtros.soloSospechosas && !esSospechosa(fila)) return false;
    if (filtros.soloSinMatch && fila.producto_id !== null) return false;
    if (busqueda === "") return true;

    const campos = [
      fila.descripcion,
      fila.producto_nombre ?? "",
      fila.codigo_externo ?? "",
      fila.codigo_interno ?? "",
    ];
    return campos.some((c) => c.toLowerCase().includes(busqueda));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Impacto: que pasa si aprieto Aplicar
// ─────────────────────────────────────────────────────────────────────────────

export interface ImpactoMoneda {
  moneda: Moneda;
  filas: number;
  costoAnterior: number;
  costoNuevo: number;
  delta: number;
  /** null cuando el costo anterior total es 0 y el porcentaje no significa nada. */
  pct: number | null;
}

export interface Impacto {
  /** Filas ACTUALIZAR: se escriben al aplicar, cambie o no el costo. */
  actualizan: number;
  /**
   * De esas, las que de verdad cambian el costo (o la moneda). «Cambian de
   * costo» contaba todas las ACTUALIZAR, incluidas las que traian el mismo
   * costo que ya tenia el producto.
   */
  cambianCosto: number;
  /** Filas ACTUALIZAR con el mismo costo que ya tiene el producto: quedan igual. */
  sinCambio: number;
  /** Productos que se dan de alta, con markup en cero. */
  crean: number;
  ignoran: number;
  /** Filas ACTUALIZAR o CREAR que conviene mirar antes de confirmar. */
  sospechosas: number;
  /** Filas ACTUALIZAR que ademas cambian la moneda del costo del producto. */
  cambiosDeMoneda: number;
  /** Filas marcadas ACTUALIZAR sin producto: el backend las rechaza con 422. */
  invalidas: number;
  /** Suma de costos, SIEMPRE separada por moneda. Solo filas comparables. */
  porMoneda: ImpactoMoneda[];
  /** De las que se actualizan, a cuantas les sube y a cuantas les baja el precio. */
  subenPrecio: number;
  bajanPrecio: number;
  /** Total de filas del import, aplicables o no. */
  total: number;
}

/**
 * Lo que va a pasar si se aplica, contado sobre las acciones ACTUALES.
 *
 * DOS DECISIONES QUE PARECEN DETALLE Y NO LO SON
 *
 * 1. Los totales de costo van separados por moneda y nunca sumados. Un total
 *    "1.250.000" que mezcla pesos con dolares es un numero que no le corresponde
 *    a nada. Quien quiera un solo numero usa `consolidarEnArs()`, que exige una
 *    cotizacion y obliga a mostrarla.
 *
 * 2. Las filas que cambian de moneda quedan FUERA del delta y se cuentan aparte.
 *    Sumar 150 USD contra 150.000 ARS daria una variacion inventada.
 */
export function calcularImpacto(filas: ReadonlyArray<FilaRevision>): Impacto {
  const acumulador = new Map<Moneda, ImpactoMoneda>();

  let actualizan = 0;
  let cambianCosto = 0;
  let sinCambio = 0;
  let crean = 0;
  let ignoran = 0;
  let sospechosas = 0;
  let cambiosDeMoneda = 0;
  let invalidas = 0;
  let subenPrecio = 0;
  let bajanPrecio = 0;

  for (const fila of filas) {
    if (fila.accion === "IGNORAR") {
      ignoran += 1;
      continue;
    }

    if (esSospechosa(fila)) sospechosas += 1;

    if (fila.accion === "CREAR") {
      crean += 1;
      continue;
    }

    // A partir de aca, ACTUALIZAR.
    actualizan += 1;
    if (fila.producto_id === null) {
      invalidas += 1;
      continue;
    }

    const precioActual = fila.precio_actual === null ? null : aNumero(fila.precio_actual);
    const precioNuevo = fila.precio_resultante === null ? null : aNumero(fila.precio_resultante);
    if (precioActual !== null && precioNuevo !== null) {
      if (precioNuevo > precioActual) subenPrecio += 1;
      else if (precioNuevo < precioActual) bajanPrecio += 1;
    }

    if (cambiaLaMoneda(fila)) {
      cambiosDeMoneda += 1;
      cambianCosto += 1;
      continue;
    }

    const anterior = fila.costo_anterior === null ? null : aNumero(fila.costo_anterior);
    if (anterior === null) {
      cambianCosto += 1;
      continue;
    }
    // La que no cambia no entra a la cuenta por moneda: sumaba a «esos N
    // productos» del diálogo, que decía 2 cuando cambiaba 1.
    if (anterior === aNumero(fila.costo)) {
      sinCambio += 1;
      continue;
    }
    cambianCosto += 1;

    const previo = acumulador.get(fila.moneda) ?? {
      moneda: fila.moneda,
      filas: 0,
      costoAnterior: 0,
      costoNuevo: 0,
      delta: 0,
      pct: null,
    };
    previo.filas += 1;
    previo.costoAnterior += anterior;
    previo.costoNuevo += aNumero(fila.costo);
    acumulador.set(fila.moneda, previo);
  }

  const porMoneda = [...acumulador.values()]
    .map((m) => ({
      ...m,
      delta: m.costoNuevo - m.costoAnterior,
      pct:
        m.costoAnterior === 0 ? null : ((m.costoNuevo - m.costoAnterior) / m.costoAnterior) * 100,
    }))
    // ARS primero: es la moneda en la que el local piensa.
    .sort((a, b) => (a.moneda === b.moneda ? 0 : a.moneda === "ARS" ? -1 : 1));

  return {
    actualizan,
    cambianCosto,
    sinCambio,
    crean,
    ignoran,
    sospechosas,
    cambiosDeMoneda,
    invalidas,
    porMoneda,
    subenPrecio,
    bajanPrecio,
    total: filas.length,
  };
}

export interface ImpactoConsolidado {
  anterior: number;
  nuevo: number;
  delta: number;
  pct: number | null;
  cotizacion: number;
}

/**
 * Un solo numero, en pesos, para el que quiere la foto entera.
 *
 * Devuelve la cotizacion usada adentro del resultado a proposito: la regla del
 * sistema es que ningun total mixto se muestra sin decir con que dolar se armo.
 */
export function consolidarEnArs(impacto: Impacto, cotizacion: number): ImpactoConsolidado | null {
  if (!Number.isFinite(cotizacion) || cotizacion <= 0) return null;
  if (impacto.porMoneda.length === 0) return null;

  let anterior = 0;
  let nuevo = 0;
  for (const m of impacto.porMoneda) {
    anterior += convertir(m.costoAnterior, m.moneda, "ARS", cotizacion);
    nuevo += convertir(m.costoNuevo, m.moneda, "ARS", cotizacion);
  }

  return {
    anterior,
    nuevo,
    delta: nuevo - anterior,
    pct: anterior === 0 ? null : ((nuevo - anterior) / anterior) * 100,
    cotizacion,
  };
}

/** El texto de la confirmacion. Se arma aca para que diga siempre lo mismo. */
export function resumenDeAplicar(impacto: Impacto): string {
  const partes: string[] = [];
  if (impacto.cambianCosto > 0) {
    partes.push(
      `${impacto.cambianCosto} ${impacto.cambianCosto === 1 ? "producto cambia" : "productos cambian"} de costo`,
    );
  }
  if (impacto.crean > 0) {
    partes.push(
      `${impacto.crean} ${impacto.crean === 1 ? "producto se da" : "productos se dan"} de alta`,
    );
  }
  // Las que se actualizan con el mismo costo quedan como estaban: se cuentan
  // con las ignoradas, no como «cambian».
  const igual = impacto.ignoran + impacto.sinCambio;
  if (partes.length === 0) {
    return impacto.actualizan > 0
      ? "Ningún costo cambia: los productos marcados ya tienen el costo de la lista."
      : "No hay ninguna fila marcada para aplicar.";
  }
  // Con los plurales: decía «Las otras 1 quedan como están», y «Las otras 0»
  // cuando no había otras.
  const resto =
    igual === 0
      ? ""
      : igual === 1
        ? " La otra queda como está."
        : ` Las otras ${igual} quedan como están.`;
  return `${partes.join(" y ")}.${resto}`;
}

/**
 * Lo que dice el cartel despues de aplicar.
 *
 * Decia «Se actualizaron N costos» con N = las filas escritas, cambiaran o
 * no: una fila con el mismo costo contaba como actualizada, y el panel de
 * antes de aplicar la habia dado como «queda como está». Ahora cuenta lo
 * mismo que el panel (`calcularImpacto` sobre las filas que se aplicaron) y
 * separa lo que cambio de lo que quedo igual, con los plurales bien.
 */
export function resumenDeAplicado(impacto: Impacto, creados: number): string {
  const n = (k: number, uno: string, varios: string) => `${k} ${k === 1 ? uno : varios}`;
  const partes = [
    `${n(impacto.cambianCosto, "costo cambió", "costos cambiaron")}`,
    ...(impacto.sinCambio > 0 ? [n(impacto.sinCambio, "quedó igual", "quedaron igual")] : []),
  ];
  const altas =
    creados > 0 ? ` y ${n(creados, "producto se dio", "productos se dieron")} de alta` : "";
  return `${partes.join(", ")}${altas}.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Validacion de la accion elegida
// ─────────────────────────────────────────────────────────────────────────────

export interface Validacion {
  valida: boolean;
  /** Por que no se puede. Se muestra donde estaria el control. */
  motivo?: string;
  /** Se puede, pero alguien tiene que saber esto antes. */
  advertencia?: string;
}

/**
 * Lo mismo que valida el backend, un paso antes.
 *
 * `PATCH /listas/import/:id/filas/:filaId` contesta 422 a "ACTUALIZAR sin
 * producto asociado". Repetir la regla aca no es duplicarla: es que el control
 * aparezca deshabilitado con el motivo al lado, en vez de dejar apretar y
 * contestar con un toast rojo.
 */
export function validarAccion(fila: FilaRevision, accion: AccionFila): Validacion {
  if (fila.aplicada) {
    return {
      valida: false,
      motivo: "Esta fila ya se aplicó. Revertí el import si querés cambiarla.",
    };
  }

  if (accion === "ACTUALIZAR") {
    if (fila.producto_id === null) {
      return {
        valida: false,
        motivo: "No tiene producto asociado. Elegí uno a mano o dejala en Ignorar.",
      };
    }
    if (cambiaLaMoneda(fila)) {
      return {
        valida: true,
        advertencia: `Hoy ese producto se costea en ${fila.moneda_anterior} y la lista lo trae en ${fila.moneda}. Aplicarla cambia la moneda del costo.`,
      };
    }
    const variacion = variacionDe(fila);
    if (variacion !== null && Math.abs(variacion) >= VARIACION_GRAVE) {
      return {
        valida: true,
        advertencia: `El costo se mueve ${formatearVariacion(variacion)}. Verificá contra el archivo antes de aplicar.`,
      };
    }
    if (fila.match_tipo === "NOMBRE") {
      return {
        valida: true,
        advertencia:
          "El match fue por parecido de nombre, no por código. Confirmá que es el mismo producto.",
      };
    }
    return { valida: true };
  }

  if (accion === "CREAR") {
    if (fila.producto_id !== null) {
      return {
        valida: true,
        advertencia: `Ya matcheó con «${fila.producto_nombre ?? "un producto del catálogo"}». Crearla dejaría dos productos iguales.`,
      };
    }
    return {
      valida: true,
      advertencia: "El producto nace con markup en cero: hay que definirlo antes de venderlo.",
    };
  }

  return { valida: true };
}

/** Las tres opciones con su validacion resuelta, para pintar el selector. */
export function opcionesDeAccion(
  fila: FilaRevision,
): Array<{ accion: AccionFila; validacion: Validacion }> {
  return ACCIONES.map((accion) => ({ accion, validacion: validarAccion(fila, accion) }));
}

// ─────────────────────────────────────────────────────────────────────────────
// La moneda de la lista
// ─────────────────────────────────────────────────────────────────────────────

export type NivelAlerta = "GRAVE" | "AVISO";

export interface AlertaMoneda {
  nivel: NivelAlerta;
  titulo: string;
  detalle: string;
}

/**
 * El error mas caro posible: aplicar una lista leyendo dolares como pesos.
 *
 * Un iPhone de US$ 900 cargado como $ 900 no rompe nada visible —el producto
 * sigue existiendo, el precio sigue calculandose— y se vende mil veces mas
 * barato que el costo hasta que alguien mira el margen. Al reves, un cargador de
 * $ 12.000 cargado como US$ 12.000 lo saca del mercado.
 *
 * Por eso la comparacion se hace contra `proveedores.moneda_lista`, que es lo
 * que el local sabe de ese proveedor, y contra las monedas que traen las filas,
 * que es lo que de verdad se va a escribir.
 */
export function alertaDeMoneda(
  imp: Pick<ImportLista, "moneda_detectada" | "proveedor_id">,
  proveedor: Pick<Proveedor, "nombre" | "moneda_lista"> | null,
  filas: ReadonlyArray<FilaRevision> = [],
): AlertaMoneda | null {
  const monedasEnFilas = new Set(filas.map((f) => f.moneda));

  if (imp.moneda_detectada === "MIXTA" || monedasEnFilas.size > 1) {
    return {
      nivel: "GRAVE",
      titulo: "La lista trae precios en las dos monedas",
      detalle:
        "Cada fila se va a escribir con la moneda que dice ella. Revisá una por una: una fila con la moneda equivocada deja un producto costeado mil veces mal.",
    };
  }

  if (proveedor === null) {
    if (imp.proveedor_id === null) {
      return {
        nivel: "AVISO",
        titulo: "El import no tiene proveedor",
        detalle:
          "Sin proveedor no corrió el emparejamiento por código de proveedor, que es el más confiable, y no hay contra qué contrastar la moneda de la lista.",
      };
    }
    return null;
  }

  const deLaLista = imp.moneda_detectada ?? [...monedasEnFilas][0] ?? null;
  if (deLaLista === null) return null;

  if (deLaLista !== proveedor.moneda_lista) {
    return {
      nivel: "GRAVE",
      titulo: `Esta lista viene en ${deLaLista} y ${proveedor.nombre} factura en ${proveedor.moneda_lista}`,
      detalle:
        "Es el error más caro que se puede aplicar acá. Verificá el archivo antes de seguir: si la moneda está mal leída, cada costo queda con la magnitud del dólar equivocada.",
    };
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// El progreso del procesamiento
// ─────────────────────────────────────────────────────────────────────────────

export type ClavePaso = "SUBIENDO" | "EXTRAYENDO" | "MATCHEANDO" | "LISTO";

export interface Paso {
  clave: ClavePaso;
  titulo: string;
  detalle: string;
}

/**
 * Los cuatro pasos reales del backend, en orden.
 *
 * No es decorado: `POST /listas/import` contesta 202 y sigue trabajando de
 * fondo, primero llamando a la Claude API (que puede tardar minutos con un PDF
 * largo) y despues corriendo `matchear_lista()`. Un spinner mudo durante tres
 * minutos hace que alguien recargue la pagina.
 */
export const PASOS: readonly Paso[] = [
  {
    clave: "SUBIENDO",
    titulo: "Subiendo el archivo",
    detalle: "Viaja al servidor. Es lo único de lo que se sabe el porcentaje exacto.",
  },
  {
    clave: "EXTRAYENDO",
    titulo: "Leyendo la lista con IA",
    detalle: "Se le pasa el archivo a Claude para que devuelva las filas. Un PDF largo tarda.",
  },
  {
    clave: "MATCHEANDO",
    titulo: "Buscando cada producto en el catálogo",
    detalle: "Primero por código de proveedor, después por código interno, después por nombre.",
  },
  { clave: "LISTO", titulo: "Listo para revisar", detalle: "Nada se escribió todavía." },
];

/**
 * En que paso esta.
 *
 * `PENDIENTE` y `PROCESANDO` no se distinguen del lado del cliente: el matching
 * corre adentro del mismo `procesarImport()` sin cambiar de estado. Se muestra
 * PROCESANDO como "extrayendo" porque es donde se va el 95% del tiempo.
 */
export function pasoActual(estado: EstadoImport | null, subiendo: boolean): number {
  if (subiendo || estado === null) return 0;
  if (estado === "PENDIENTE" || estado === "PROCESANDO") return 1;
  return 3;
}

export function estaTrabajando(estado: EstadoImport | null): boolean {
  return estado === "PENDIENTE" || estado === "PROCESANDO";
}

export function sePuedeAplicar(imp: ImportLista | null, impacto: Impacto): boolean {
  if (imp === null) return false;
  if (imp.estado !== "COMPLETADO") return false;
  if (impacto.invalidas > 0) return false;
  return impacto.actualizan + impacto.crean > 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Validacion del archivo, antes de gastar la subida
// ─────────────────────────────────────────────────────────────────────────────

export function extensionDe(nombre: string): string {
  const punto = nombre.lastIndexOf(".");
  return punto === -1 ? "" : nombre.slice(punto).toLowerCase();
}

/**
 * Rechaza lo que el backend va a rechazar igual, sin subir 24 MB al pedo.
 *
 * El mensaje dice el limite y los formatos: "Formato no soportado" a secas
 * obliga a adivinar cual era el problema.
 */
export function validarArchivo(archivo: File): Validacion {
  if (archivo.size === 0) {
    return { valida: false, motivo: "El archivo está vacío." };
  }
  if (archivo.size > LIMITE_BYTES_ARCHIVO) {
    return {
      valida: false,
      motivo: `Pesa ${formatearBytes(archivo.size)} y el límite es ${formatearBytes(LIMITE_BYTES_ARCHIVO)}. Dividilo en partes.`,
    };
  }
  const ext = extensionDe(archivo.name);
  if (!(EXTENSIONES_ACEPTADAS as readonly string[]).includes(ext)) {
    return {
      valida: false,
      motivo: `No se puede leer un «${ext || "archivo sin extensión"}». Aceptamos PDF, Excel, CSV, texto e imágenes.`,
    };
  }
  return { valida: true };
}

// ─────────────────────────────────────────────────────────────────────────────
// Formato
// ─────────────────────────────────────────────────────────────────────────────

/** "+12,5 %" / "-3,0 %". El signo va explicito: un aumento y una baja no se leen igual. */
export function formatearVariacion(pct: number): string {
  const signo = pct > 0 ? "+" : "";
  return `${signo}${formatearNumero(pct, 1)} %`;
}

export function formatearBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${formatearNumero(bytes / 1024, 0)} KB`;
  return `${formatearNumero(bytes / (1024 * 1024), 1)} MB`;
}

export function formatearDuracion(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const segundos = ms / 1000;
  if (segundos < 60) return `${formatearNumero(segundos, 1)} s`;
  const minutos = Math.floor(segundos / 60);
  return `${minutos} min ${Math.round(segundos - minutos * 60)} s`;
}

// ─────────────────────────────────────────────────────────────────────────────
// La lista en una sola pantalla (0150)
// ─────────────────────────────────────────────────────────────────────────────
//
// Como la compra en una pantalla: lo reconocido por codigo no se pregunta, lo
// muy parecido se confirma en bloque, lo poco parecido de a uno, y lo que no
// esta en el catalogo queda afuera (la lista solo actualiza costos). A la
// vista queda solo lo que sube mucho; lo demas, en grupos plegados. Salio del
// prototipo que probaron usuarios simulados el 29/10/2026.

/** Desde cuanto un parecido por nombre se confirma en bloque. */
export const PARECIDO_EN_BLOQUE = 0.7;
/** Desde cuanto una suba se frena: suele ser un decimal mal leido. */
export const SUBA_QUE_FRENA = 0.5;

export type MarcaFilaLista =
  "SUBE_MUCHO" | "SUBE" | "NO_BAJA" | "SIN_CAMBIOS" | "A_MANO" | "NO_ACTUALIZA" | "NO_LO_TENES";

export const TEXTO_MARCA_LISTA: Record<MarcaFilaLista, string> = {
  SUBE_MUCHO: "sube mucho",
  SUBE: "sube",
  NO_BAJA: "no baja",
  SIN_CAMBIOS: "sin cambios",
  A_MANO: "precio a mano",
  NO_ACTUALIZA: "no se actualiza",
  NO_LO_TENES: "no lo tenés",
};

/** Reconocida solo por el nombre y sin decidir: se pregunta. */
export function esParecidoSinConfirmar(f: FilaRevision): boolean {
  return !f.aplicada && f.match_tipo === "NOMBRE" && f.producto_id !== null;
}

/** De las sin confirmar, las que van en bloque (las muy parecidas). */
export function vaEnBloque(f: FilaRevision): boolean {
  return esParecidoSinConfirmar(f) && aNumero(f.match_confianza ?? 0) >= PARECIDO_EN_BLOQUE;
}

/** Cuanto cambia el costo, en la misma moneda. Null si no hay con que comparar. */
export function variacionDeCosto(f: FilaRevision): number | null {
  if (f.costo_anterior === null || f.moneda_anterior !== f.moneda) return null;
  const antes = aNumero(f.costo_anterior);
  return antes > 0 ? (aNumero(f.costo) - antes) / antes : null;
}

export function marcaDeFilaLista(
  f: FilaRevision,
  subasMiradas: ReadonlySet<number>,
): MarcaFilaLista {
  if (f.producto_id === null) return "NO_LO_TENES";
  if (f.accion !== "ACTUALIZAR") return "NO_ACTUALIZA";
  const variacion = variacionDeCosto(f);
  if (variacion !== null && variacion > SUBA_QUE_FRENA && !subasMiradas.has(f.id))
    return "SUBE_MUCHO";
  if (f.precio_manual != null) return "A_MANO";
  const antes = f.precio_actual === null ? null : aNumero(f.precio_actual);
  const despues = f.precio_resultante === null ? null : aNumero(f.precio_resultante);
  if (antes !== null && despues !== null && despues > antes) return "SUBE";
  if (variacion !== null && variacion < 0) return "NO_BAJA";
  return "SIN_CAMBIOS";
}

/** Si el costo de la fila es distinto del que tiene el producto. */
export function cambiaElCosto(f: FilaRevision): boolean {
  return (
    f.costo_anterior === null ||
    aNumero(f.costo_anterior) !== aNumero(f.costo) ||
    f.moneda_anterior !== f.moneda
  );
}
